// client/src/utils/listingLinks.js — what the address of a found property links to.
//
// Owner, 25 Oct 2026: "all searches addresses are linked to commercial cafe only — and most of
// them never have any info." Many CommercialCafe / CommercialSearch links are BUILDING pages
// (…/commercial-property/us/il/<town>/<address>/): the site keeps a page for every building it
// knows, with or without space for lease, so the link often shows nothing available.
//
// Every screen that shows a found property (Site search panel on the client record, My Site
// Searches, Zoning search, the client portal) uses this one file:
//   classifyListingUrl(url)  what kind of page a saved link is: a listing, a building page,
//                            a search page, or nothing usable.
//   primaryLink(c)           the address link and the words under it (never calls a building
//                            page "the listing").
//   lookupLinks(c, opts)     "Look it up on:" one-click links. Each is a Google search limited to
//                            that site with the street address in quotes, so it lands on that
//                            site's own page for THIS address when the site has one — reliable
//                            without guessing each site's URL scheme, and the server never
//                            visits those sites (a person clicks; lib/fetchGuard.js).
//   townSearchLinks(c, opts) the site's own whole-town search (templates from outsideSearch.js).
//   siteKindFor(uses)        'commercial' | 'residential' | 'unknown' from the search's use(s).
//
// The only import is outsideSearch.js (no React), so scripts/test-listing-links.js can load
// both files by stripping `import` / `export`.
import { DEFAULT_SITES, buildUrl } from './outsideSearch';

export const SOURCE_NAME = {
    loopnet: 'LoopNet', crexi: 'Crexi', cityfeet: 'CityFeet', commercialcafe: 'CommercialCafe',
    commercialsearch: 'CommercialSearch', zillow: 'Zillow', realtor: 'Realtor.com', apartments: 'Apartments.com',
    forrent: 'ForRent', redfin: 'Redfin', trulia: 'Trulia', homes: 'Homes.com',
    fsbo: 'FSBO.com', forsalebyowner: 'ForSaleByOwner.com', craigslist: 'Craigslist', other: 'Other',
};

export const COMMERCIAL_SITES = [
    { key: 'loopnet', label: 'LoopNet', domain: 'loopnet.com' },
    { key: 'crexi', label: 'Crexi', domain: 'crexi.com' },
    { key: 'cityfeet', label: 'CityFeet', domain: 'cityfeet.com' },
];
// Owner, 27 Oct 2026: CommercialCafe / CommercialSearch pages "don't show anything" — they are
// no longer offered as a lookup, and an address found there opens a Google search instead.
const EMPTY_PAGE_SITES = ['commercialcafe', 'commercialsearch'];
export const RESIDENTIAL_SITES = [
    { key: 'zillow', label: 'Zillow', domain: 'zillow.com' },
    { key: 'realtor', label: 'Realtor.com', domain: 'realtor.com' },
    { key: 'redfin', label: 'Redfin', domain: 'redfin.com' },
    { key: 'apartments', label: 'Apartments.com', domain: 'apartments.com' },
    { key: 'forrent', label: 'ForRent', domain: 'forrent.com' },
];

// For sale by owner homes (owner, 1 Nov 2026: "look for sale by owner homes too"): a by-owner find
// also gets these lookups, and Zillow's own "for sale by owner" page for its town. Same as the
// email (lib/residentialSearch.js FSBO_LOOKUPS / zillowFsboUrl).
export const FSBO_SITES = [
    { key: 'fsbo', label: 'FSBO.com', domain: 'fsbo.com' },
    { key: 'forsalebyowner', label: 'ForSaleByOwner.com', domain: 'forsalebyowner.com' },
];
/** Zillow's by-owner homes for the find's town, e.g. https://www.zillow.com/glenview-il/fsbo/ */
export function zillowFsboUrl(c, state = '') {
    const slug = [String((c && (c.municipality || c.town || c.city)) || '').trim(), (c && c.state) || state || 'IL']
        .filter(Boolean).join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return String((c && (c.municipality || c.town || c.city)) || '').trim() ? `https://www.zillow.com/${slug}/fsbo/` : '';
}

const SITE_DOMAINS = [
    ['loopnet', 'loopnet.com'], ['crexi', 'crexi.com'], ['cityfeet', 'cityfeet.com'],
    ['commercialcafe', 'commercialcafe.com'], ['commercialsearch', 'commercialsearch.com'],
    ['zillow', 'zillow.com'], ['realtor', 'realtor.com'], ['apartments', 'apartments.com'], ['forrent', 'forrent.com'],
    ['redfin', 'redfin.com'], ['trulia', 'trulia.com'], ['homes', 'homes.com'],
    // For sale by owner (1 Nov 2026): FSBO.com, ForSaleByOwner.com, Craigslist "real estate - by owner".
    ['fsbo', 'fsbo.com'], ['forsalebyowner', 'forsalebyowner.com'], ['craigslist', 'craigslist.org'],
];

const parseUrl = (url) => {
    const u = typeof url === 'string' ? url.trim() : '';
    if (!/^https?:\/\//i.test(u)) return null;
    try { return new URL(u); } catch { return null; }
};

/** The site key a URL belongs to ('loopnet' …), 'google' for a Google search, or 'other'. */
export function siteOfUrl(url) {
    const p = parseUrl(url);
    if (!p) return null;
    const host = p.hostname.toLowerCase().replace(/\.+$/, '');
    if (/(^|\.)google\.[a-z.]+$/.test(host)) return 'google';
    const hit = SITE_DOMAINS.find(([, d]) => host === d || host.endsWith(`.${d}`));
    return hit ? hit[0] : 'other';
}

// A listing number somewhere in the address: ?listingid=…, /listing/123…, /spaces/…, or a long
// numeric path part. Building pages on CommercialCafe / CommercialSearch carry none of these.
const LISTING_ID = /[?&](listing|listingid|spaceid|space|lid)=|\/(listing|listings|space|spaces|suite)\/|\/\d{5,}(\/|$)/i;

/**
 * What kind of page a saved link is.
 *   listing  — a page for an available space / unit (or a site we cannot tell apart: given
 *              the benefit of the doubt)
 *   building — a building page that may list no available space (CommercialCafe /
 *              CommercialSearch "commercial-property" pages, LoopNet /property/, Zillow /b/)
 *   search   — a town or category results page, or a web search: not this address
 *   none     — no usable link
 * @returns {{kind: string, site: string|null}}
 */
export function classifyListingUrl(url) {
    const p = parseUrl(url);
    if (!p) return { kind: 'none', site: null };
    const site = siteOfUrl(url);
    const path = p.pathname.toLowerCase();
    const full = `${path}${p.search.toLowerCase()}`;
    const parts = path.split('/').filter(Boolean);
    switch (site) {
    case 'google':
        return { kind: 'search', site };
    case 'commercialcafe':
    case 'commercialsearch':
        // …/commercial-property/us/il/northbrook/310-340-melvin-dr/ is the building page.
        if (parts[0] === 'commercial-property') return { kind: LISTING_ID.test(full) ? 'listing' : 'building', site };
        // …/commercial-real-estate/us/il/deerfield/, …/office/us/il/northbrook/, the home page.
        return { kind: LISTING_ID.test(full) ? 'listing' : 'search', site };
    case 'loopnet':
        if (parts[0] === 'listing') return { kind: 'listing', site };
        if (parts[0] === 'property') return { kind: 'building', site };
        if (parts[0] === 'search' || !parts.length) return { kind: 'search', site };
        return { kind: 'listing', site };
    case 'crexi':
        // /properties/12345/… and /lease/properties/12345/… are listings; /properties/IL/Oak-Park is a town search.
        if (parts.some((x) => /^\d+$/.test(x))) return { kind: 'listing', site };
        if (!parts.length || parts.includes('properties')) return { kind: 'search', site };
        return { kind: 'listing', site };
    case 'cityfeet':
        if (!parts.length || /commercial-real-estate-for-(sale|lease)|^search$/.test(parts[parts.length - 1])) return { kind: 'search', site };
        return { kind: 'listing', site };
    case 'zillow':
        if (parts[0] === 'homedetails' || parts[0] === 'apartments') return { kind: 'listing', site };
        if (parts[0] === 'b') return { kind: 'building', site };
        return { kind: 'search', site };
    case 'realtor':
        if (/detail|details/.test(parts[0] || '') || parts[1] === 'details') return { kind: 'listing', site };
        return { kind: 'search', site };
    case 'apartments':
        // apartments.com/oak-park-il/ is the town; apartments.com/<property-slug>/<id>/ a listing.
        return { kind: parts.length >= 2 ? 'listing' : 'search', site };
    case 'forrent':
        return { kind: !parts.length || parts[0] === 'find' ? 'search' : 'listing', site };
    case 'redfin':
        // redfin.com/IL/Glenview/123-Main-St-60025/home/12345 is a home; /city/… a town search.
        return { kind: parts.includes('home') || parts.includes('unit') ? 'listing' : 'search', site };
    case 'trulia':
        return { kind: parts[0] === 'p' || parts[0] === 'home' || parts[0] === 'rental' ? 'listing' : 'search', site };
    case 'homes':
        return { kind: parts[0] === 'property' ? 'listing' : 'search', site };
    case 'fsbo':
    case 'forsalebyowner':
        // the home page or a search / town page is not one home; a listing page carries an id.
        if (!parts.length || /^(search|homes-for-sale|state|city)$/.test(parts[0]) || (parts.length <= 2 && !/\d{4,}/.test(path))) return { kind: 'search', site };
        return { kind: 'listing', site };
    case 'craigslist':
        // chicago.craigslist.org/search/reo is the by-owner list; …/reo/d/<slug>/<id>.html one home.
        return { kind: parts[0] === 'search' || !parts.length ? 'search' : 'listing', site };
    default:
        return { kind: 'listing', site };
    }
}

const siteLabel = (site, source) => SOURCE_NAME[site] || SOURCE_NAME[source] || 'the listing site';

const addressOnly = (c) => String((c && c.address) || '').split(',')[0].trim();
const townOf = (c) => String((c && (c.municipality || c.town || c.city)) || '').trim();

/** A Google web search for the whole address. */
export function webSearchUrl(c, state = '') {
    const q = [addressOnly(c), townOf(c), (c && c.state) || state].filter(Boolean).join(', ');
    return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

/**
 * The address link. `kind` says what the saved link is; `href` is where the address goes.
 * A building page is still linked (photos, the building's details) but never called the listing.
 */
export function primaryLink(c, state = '') {
    const url = typeof (c && c.source_url) === 'string' ? c.source_url.trim() : '';
    const { kind, site } = classifyListingUrl(url);
    const name = siteLabel(site, c && c.source);
    if (EMPTY_PAGE_SITES.includes(site)) {
        return { href: webSearchUrl(c, state), kind: 'search', label: 'Search the web for this address', short: 'Web search', foundOn: { href: url, label: `found on ${name}` } };
    }
    if (kind === 'listing') return { href: url, kind, label: `Listing on ${name}`, short: name };
    if (kind === 'building') return { href: url, kind, label: `Building page on ${name} (may show no space)`, short: `${name} building page` };
    const web = webSearchUrl(c, state);
    if (kind === 'search') return { href: web, kind, label: 'Search the web for this address', short: 'Web search', foundOn: { href: url, label: `found on a ${name} search page` } };
    return { href: web, kind, label: 'Search the web for this address', short: 'Web search' };
}

/** 'residential' for homes / apartments, 'commercial' for a business use, 'unknown' when not said. */
export function siteKindFor(uses) {
    const list = (Array.isArray(uses) ? uses : [uses]).map((u) => String(u || '').trim()).filter(Boolean);
    if (!list.length) return 'unknown';
    const res = /\b(apartments?|residential|residence|rental housing|single[- ]family|multi[- ]?family|condo(minium)?s?|town ?homes?|town ?houses?|dwellings?)\b/i;
    if (list.every((u) => res.test(u))) return 'residential';
    return 'commercial';
}

/** Google, limited to one site, for this street address in this town. */
export function siteSearchUrl(domain, c, state = '') {
    const addr = addressOnly(c);
    if (!addr) return '';
    const q = [`site:${domain}`, `"${addr.replace(/"/g, '')}"`, townOf(c), (c && c.state) || state].filter(Boolean).join(' ');
    return `https://www.google.com/search?q=${encodeURIComponent(q).replace(/%20/g, '+')}`;
}

/**
 * "Look it up on:" links. { primary: [...], more: [...] } — primary is the set for the search's
 * kind (commercial when unknown); more is the other set. Each item: { key, label, href }.
 */
export function lookupLinks(c, { kind = 'unknown', state = '' } = {}) {
    const make = (sites) => sites.map((s) => ({ key: s.key, label: s.label, href: siteSearchUrl(s.domain, c, state) })).filter((x) => x.href);
    const residential = kind === 'residential';
    // Plain Google for the address sits beside the three commercial sites (owner, 27 Oct 2026).
    const google = addressOnly(c) ? [{ key: 'google', label: 'Google', href: webSearchUrl(c, state) }] : [];
    // A for-sale-by-owner home also gets FSBO.com, ForSaleByOwner.com and Zillow's by-owner page.
    const fsbo = residential && c && c.seller_type === 'owner' && addressOnly(c)
        ? [...make(FSBO_SITES), ...(zillowFsboUrl(c, state) ? [{ key: 'zillow_fsbo', label: 'Zillow FSBO', href: zillowFsboUrl(c, state) }] : [])]
        : [];
    return {
        primary: [...make(residential ? RESIDENTIAL_SITES : COMMERCIAL_SITES), ...fsbo, ...google],
        more: make(residential ? COMMERCIAL_SITES : RESIDENTIAL_SITES),
    };
}

/** The sites' own whole-town searches (outsideSearch.js templates), for the town of this property. */
export function townSearchLinks(c, { kind = 'unknown', state = '', intent = '' } = {}) {
    const deal = /purchase|buy|sale/i.test(String(intent || '')) ? 'sale' : 'rent';
    const st = (c && c.state) || state;
    const want = kind === 'residential' ? 'residential' : 'commercial';
    const order = (s) => (s.kind === want ? 0 : 1);
    return DEFAULT_SITES.filter((s) => !EMPTY_PAGE_SITES.includes(s.key)).sort((a, b) => order(a) - order(b))
        .map((s) => ({ key: s.key, label: s.label, kind: s.kind, href: buildUrl(s[deal] || s.rent || s.sale, { city: townOf(c), state: st }) }))
        .filter((x) => x.href);
}
